import React from 'react';
import { FileSpreadsheet, Code2, Calculator, Tv, GraduationCap, Users, ShoppingBag, BarChart3, Sparkles, Globe, MessageSquarePlus } from 'lucide-react';

export const Services = () => {
  const serviceCategories = [
    {
      title: 'Live Streaming',
      eyebrowClass: 'eyebrow',
      services: [
        { icon: Tv, title: 'OBS Stream Widgets', description: 'Professional web-hosted overlays, chat displays, scoreboards, and alert banners.', testid: 'service-obs-widgets' },
      ],
    },
    {
      title: 'Discover & Learn',
      eyebrowClass: 'eyebrow eyebrow-blue',
      isLearningTrack: true,
      services: [
        { icon: Users, title: 'Group Training Sessions', description: 'Cost-effective group classes covering live streaming, Excel, and seller strategies — learn alongside others.', testid: 'service-group-training' },
        { icon: GraduationCap, title: '1:1 Personal Sessions', description: 'One-on-one time tailored to your goals. We share what we know, and learn alongside you when needed.', testid: 'service-personal-coaching' },
        { icon: ShoppingBag, title: 'TikTok & eBay LIVE Training', description: 'Master live selling on TikTok and eBay. Boost engagement, conversions, and sales.', testid: 'service-live-selling', comingSoon: true },
      ],
    },
    {
      title: 'Excel & Data Solutions',
      eyebrowClass: 'eyebrow',
      services: [
        { icon: FileSpreadsheet, title: 'Custom Excel Sheets', description: 'Tailored spreadsheets for inventory, finance, dashboards — built for your workflow.', testid: 'service-excel-sheets' },
        { icon: Calculator, title: 'Formula Assistance', description: 'VLOOKUP, INDEX/MATCH, complex arrays — we untangle the toughest formulas.', testid: 'service-formulas' },
        { icon: Code2, title: 'VBA Automation', description: 'Turn hours of manual work into one click. Custom macros that save real time.', testid: 'service-vba' },
        { icon: BarChart3, title: 'Sales Data Reports', description: 'Parse and visualize sales history, pending payments, and revenue trends.', testid: 'service-sales-reports' },
      ],
    },
    {
      title: 'Web & Digital',
      eyebrowClass: 'eyebrow eyebrow-blue',
      services: [
        { icon: Globe, title: 'Custom Website Building', description: 'Need a website? We do that too. From simple landing pages to full-featured business sites — designed, built, and live.', testid: 'service-website-building' },
      ],
    },
  ];

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const requestTrainingTopic = () => {
    const intent = 'a training topic — here is what I would like you to cover:';
    try {
      sessionStorage.setItem('lab21g.contactIntent', intent);
    } catch (e) {
      // no-op; the form still works without prefill
    }
    // Dispatch event so the (already-mounted) ContactForm on this page prefills immediately.
    window.dispatchEvent(new CustomEvent('lab21g:contact-intent', { detail: { intent } }));
    scrollToContact();
  };

  return (
    <section id="services" className="py-24 px-4 sm:px-6 lg:px-8 relative" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="eyebrow" data-testid="services-badge">What We Do</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4 mb-4 text-strong" data-testid="services-heading">
            Solutions That Power Your Business
          </h2>
          <p className="text-base text-muted max-w-2xl mx-auto" data-testid="services-description">
            From live stream widgets to Excel automation to hands-on training — discover, learn, and train with us.
          </p>
        </div>

        <div className="space-y-16">
          {serviceCategories.map((category) => (
            <div key={category.title} data-testid={`category-${category.title}`}>
              <div className="flex items-center space-x-4 mb-8">
                <div className="divider-line flex-1"></div>
                <h3 className={`${category.eyebrowClass} whitespace-nowrap`}>{category.title}</h3>
                <div className="divider-line flex-1"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {category.services.map((service) => {
                  const Icon = service.icon;
                  const isComing = service.comingSoon;
                  return (
                    <button
                      type="button"
                      onClick={isComing ? requestTrainingTopic : scrollToContact}
                      key={service.testid}
                      className="text-left card-surface p-6 cursor-pointer focus:outline-none group relative overflow-hidden"
                      data-testid={service.testid}
                      aria-label={isComing ? `${service.title} — coming soon. Tap to request a training topic.` : service.title}
                    >
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-11 h-11 rounded-xl border border-default surface-1 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                          <Icon className="w-5 h-5 accent-green" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-base font-semibold mb-1.5 text-strong">{service.title}</h4>
                          <p className="text-sm text-muted leading-relaxed">{service.description}</p>
                        </div>
                      </div>

                      {isComing && (
                        <div
                          className="absolute inset-0 flex items-center justify-center pointer-events-none"
                          style={{
                            background:
                              'linear-gradient(135deg, rgba(15,17,19,0.55) 0%, rgba(15,17,19,0.35) 100%)',
                            backdropFilter: 'blur(2px)',
                            WebkitBackdropFilter: 'blur(2px)',
                          }}
                          data-testid={`${service.testid}-coming-soon`}
                        >
                          <div
                            className="px-4 py-2 rounded-full border text-xs font-semibold tracking-[0.18em] uppercase"
                            style={{
                              background: 'rgba(15,17,19,0.85)',
                              borderColor: 'rgba(57,255,20,0.4)',
                              color: '#39FF14',
                              boxShadow: '0 0 24px rgba(57,255,20,0.15)',
                            }}
                          >
                            Coming Soon
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {category.isLearningTrack && (
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={requestTrainingTopic}
                    className="btn-primary inline-flex items-center space-x-2 px-5 py-2.5 text-sm"
                    data-testid="request-training-topic-btn"
                  >
                    <MessageSquarePlus className="w-4 h-4" />
                    <span>Request a training topic</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <a
            href="/#contact"
            className="inline-flex flex-wrap items-center justify-center gap-3 card-surface px-6 py-4 hover:border-emphasis transition-colors cursor-pointer"
            data-testid="services-cta-banner"
            style={{ borderColor: 'rgba(57, 255, 20, 0.25)' }}
          >
            <Sparkles className="w-5 h-5 accent-green" />
            <span className="text-base sm:text-lg font-semibold text-strong">
              Got a project in mind? Let&apos;s build it.
            </span>
            <Sparkles className="w-5 h-5 accent-green" />
          </a>
          <p className="text-sm text-muted mt-4">
            Tap the banner above to jump to the contact form — tell us what you need, and we&apos;ll make it happen.
          </p>
        </div>
      </div>
    </section>
  );
};
